import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker, DrawingManager } from '@react-google-maps/api';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { createGlobalStyle } from 'styled-components';

const GlobalPacStyle = createGlobalStyle`
  .pac-container {
    z-index: 9999999 !important;
  }
`;

const containerStyle = {
  width: '800px',
  height: '600px'
};

const centerDefault = {
  lat: 49.549602965584064,
  lng: 8.597027109190737
};

const MapModal = ({ visible, onClose, onSave, defaultLat, defaultLng, isLoaded }) => {
  const [mode, setMode] = useState('point');
  const [markerPosition, setMarkerPosition] = useState(null);
  const [polygon, setPolygon] = useState(null);
  const [area, setArea] = useState(null);
  const [mapCenter, setMapCenter] = useState(centerDefault);
  const [resetKey, setResetKey] = useState(0);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (visible) {
      if (defaultLat && defaultLng) {
        const initial = { lat: parseFloat(defaultLat), lng: parseFloat(defaultLng) };
        setMarkerPosition(initial);
        setMapCenter(initial);
      } else {
        setMarkerPosition(null);
        setMapCenter(centerDefault);
      }
      setPolygon(null);
      setArea(null);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    }
  }, [visible, defaultLat, defaultLng, resetKey]);

  const initializeAutocomplete = useCallback(() => {
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      searchInputRef.current &&
      !autocompleteRef.current
    ) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { fields: ['geometry', 'formatted_address'] }
      );

      const listener = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry && place.geometry.location) {
          const newPos = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          if (mode === 'point') {
            setMarkerPosition(newPos);
          }
          setMapCenter(newPos);
        }
      });

      return listener;
    }
  }, [mode]);

  useEffect(() => {
    let listener;
    if (visible && isLoaded) {
      listener = initializeAutocomplete();
    }
    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
      autocompleteRef.current = null;
    };
  }, [visible, initializeAutocomplete, isLoaded]);

  const onMapClick = useCallback((event) => {
    if (mode === 'point') {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarkerPosition({ lat, lng });
      setPolygon(null);
      setArea(null);
    }
  }, [mode]);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const onPolygonComplete = useCallback((poly) => {
    const areaInSquareMeters = window.google.maps.geometry.spherical.computeArea(poly.getPath());
    setArea(areaInSquareMeters);
    setPolygon(poly);
    setMarkerPosition(null);
  }, []);

  const switchMode = (newMode) => {
    if (mode === 'polygon' && newMode === 'point') {
      setResetKey((prev) => prev + 1);
    }
    setMode(newMode);
    setMarkerPosition(null);
    setPolygon(null);
    setArea(null);
    if (polygon) {
      polygon.setMap(null);
    }
  };

  if (!visible || !isLoaded) return null;

  return (
    <>
      <GlobalPacStyle />
      <Dialog title="Einsatzort wählen" onClose={onClose} open={visible}>
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Ort suchen..."
              style={{
                width: containerStyle.width,
                padding: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <Button
              onClick={() => switchMode('point')}
              themeColor={mode === 'point' ? 'primary' : 'base'}
            >
              Standort auswählen
            </Button>
            <Button
              onClick={() => switchMode('polygon')}
              themeColor={mode === 'polygon' ? 'primary' : 'base'}
              style={{ marginLeft: '10px' }}
            >
              Fläche messen
            </Button>
          </div>
          <GoogleMap
            key={resetKey}
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={12}
            onClick={onMapClick}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {mode === 'point' && markerPosition && <Marker position={markerPosition} />}
            {mode === 'polygon' && (
              <DrawingManager
                onPolygonComplete={onPolygonComplete}
                options={{
                  drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
                  drawingControl: false,
                  polygonOptions: {
                    fillColor: '#ff0000',
                    fillOpacity: 0.35,
                    strokeWeight: 2,
                    clickable: true,
                    editable: true,
                  },
                }}
              />
            )}
          </GoogleMap>
          {area && mode === 'polygon' && (
            <div style={{ marginTop: '10px', marginBottom: '20px' }}>
              Fläche: {(area / 1000000).toFixed(2)} km²
            </div>
          )}
          <div
            style={{
              backgroundColor: 'white',
              width: containerStyle.width,
              padding: '20px 0px 10px 0px', // 20px rechts hinzugefügt
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button onClick={onClose}>Abbrechen</Button>
            <Button
              themeColor="primary"
              onClick={() => {
                if (mode === 'point' && markerPosition) {
                  onSave(markerPosition.lat, markerPosition.lng);
                } else if (mode === 'polygon' && polygon) {
                  const bounds = new window.google.maps.LatLngBounds();
                  polygon.getPath().forEach((latLng) => bounds.extend(latLng));
                  const center = bounds.getCenter();
                  onSave(center.lat(), center.lng());
                }
              }}
              disabled={(!markerPosition && mode === 'point') || (!polygon && mode === 'polygon')}
            >
              Übernehmen
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default MapModal;